import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { MultiSelectInput } from '@/components/ui/multi-select-input';
import { PlusCircleIcon, XMarkIcon } from '@heroicons/react/16/solid';
import type { BaseInstance } from '@pigello/pigello-matrix';
import { useEffect, useRef, useState } from 'react';
import { useWatch, type Path } from 'react-hook-form';
import type { FieldProps } from '../types';
import { DescriptionTooltip } from './description-tooltip';
import { Switch } from '@/components/ui/switch';
import { motion, AnimatePresence } from 'framer-motion';
import { useResizeObserver } from 'usehooks-ts';
import { isArray } from 'lodash';
// SMS = 0
// EMAIL = 1
// E_INBOX = 2
// POSTAL = 3
// E_BANK = 4
// E_INVOICE = 5

const reminderAndDebtDoNotInclude = [4, 5];

export const distributionDefaultValues = [
  {
    v: 0,
    d: 'SMS',
  },
  {
    v: 1,
    d: 'E-post',
  },
  {
    v: 2,
    d: 'Digital brevlåda',
  },
  {
    v: 3,
    d: 'Post',
  },
  {
    v: 4,
    d: 'E-faktura (bank)',
  },
  {
    v: 5,
    d: 'E-faktura (PEPPOL)',
  },
];

const HEADER_DEFAULT_HEIGHT = 24;
const TOTAL_HEIGHT_PADDING = 36;

export function DistributionInstruction<
  Instance extends BaseInstance = BaseInstance,
>({
  formField,
  name,
  placeholder,
  label,
  description,
  className,
  disabled,
  canHandleField,
  allowedDistributionMethods,
}: FieldProps<Instance>) {
  const arr = useWatch({
    control: formField.control,
    name: (name ?? formField.name) as string,
  });
  const [selected, setSelected] = useState<number[][]>();
  const [isFieldActive, setIsFieldActive] = useState(false);

  const availableDistributionMethods = allowedDistributionMethods
    ? distributionDefaultValues.filter(({ v }) =>
        allowedDistributionMethods.includes(v)
      )
    : distributionDefaultValues;

  useEffect(() => {
    const values =
      arr?.length === 0 || !isArray(arr) || typeof arr === 'undefined'
        ? [[]]
        : (arr as Array<number[]>);

    setSelected([...(values ?? [])]);
    if (values?.some((instructionArr) => instructionArr.length > 0)) {
      setIsFieldActive(true);
    }
  }, [arr]);

  const childRef = useRef<HTMLDivElement>(null);
  const childSize = useResizeObserver({
    ref: childRef as any,
    box: 'border-box',
  });

  const headerRef = useRef<HTMLDivElement>(null);
  const headerSize = useResizeObserver({
    ref: headerRef as any,
    box: 'border-box',
  });

  return (
    <Card className='col-span-full p-0'>
      <motion.div
        initial={{
          height:
            headerSize.height ?? HEADER_DEFAULT_HEIGHT + TOTAL_HEIGHT_PADDING,
        }}
        animate={{
          height:
            ((isFieldActive ? childSize.height : headerSize.height) ??
              HEADER_DEFAULT_HEIGHT) + TOTAL_HEIGHT_PADDING,
        }}
        className='overflow-hidden p-4'
      >
        <FormField
          control={formField.control}
          rules={{
            required: formField.required && 'Detta fält är obligatoriskt',
          }}
          name={name as Path<Instance>}
          render={({ field }) => {
            const flattenedSelected = (field.value?.flat() as number[]) ?? [];
            const defaultValues = availableDistributionMethods.filter((d) => {
              const currentlySelected = flattenedSelected.includes(d.v);
              if (
                field.name.includes('debt') ||
                field.name.includes('reminder')
              ) {
                return (
                  !currentlySelected &&
                  !reminderAndDebtDoNotInclude.includes(d.v)
                );
              }
              return !currentlySelected;
            });

            return (
              <div ref={childRef} className='flex flex-col gap-2'>
                <div ref={headerRef} className='flex w-full justify-between'>
                  <span className='text-sm'>{label ?? formField.label}</span>
                  <Switch
                    checked={isFieldActive}
                    onCheckedChange={(checked) => {
                      if (!checked) {
                        field.onChange([]);
                      }
                      setIsFieldActive(checked);
                    }}
                  />
                </div>
                <AnimatePresence>
                  {isFieldActive && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className='flex flex-col gap-2'
                    >
                      <FormItem className={className}>
                        {selected?.map((num, i) => (
                          <div
                            className='grid gap-2'
                            key={`${String(formField.name)}-${num.join('-')}-${i})}`}
                          >
                            <div className='flex items-center'>
                              <FormLabel>
                                {i === 0
                                  ? 'Försök först med'
                                  : 'Om alla ovanstående misslyckas, försök istället med'}{' '}
                                {formField.required && <span>*</span>}
                              </FormLabel>
                              {(description || formField.description) && (
                                <DescriptionTooltip
                                  description={
                                    description ?? formField.description
                                  }
                                />
                              )}
                            </div>
                            <FormControl>
                              <div className='relative'>
                                <MultiSelectInput
                                  placeholder={placeholder ?? `Välj metod`}
                                  onSelected={(items) => {
                                    const values = items.map(
                                      (i) => i.v
                                    ) as number[];
                                    const newValues = [...(selected ?? [])];
                                    newValues[i] = values;
                                    if (newValues.flat().length === 0) {
                                      field.onChange([]);
                                    } else {
                                      field.onChange(newValues);
                                    }
                                  }}
                                  values={
                                    num?.map((n) => ({
                                      v: n,
                                      d:
                                        availableDistributionMethods.find(
                                          (d) => d.v === n
                                        )?.d ?? '',
                                    })) ?? []
                                  }
                                  defaultValues={defaultValues}
                                  disabled={
                                    disabled ??
                                    field.disabled ??
                                    !canHandleField
                                  }
                                />
                                {i !== 0 && (
                                  <Button
                                    type='button'
                                    className='absolute -top-2 -right-2 rounded-full'
                                    disabled={
                                      disabled ??
                                      field.disabled ??
                                      !canHandleField
                                    }
                                    onClick={() => {
                                      const newValues = selected.toSpliced(
                                        i,
                                        1
                                      );
                                      if (newValues.flat().length === 0) {
                                        field.onChange([]);
                                      } else {
                                        field.onChange(newValues);
                                      }
                                      setSelected(newValues);
                                    }}
                                    variant={'secondary'}
                                    size={'icon'}
                                  >
                                    <XMarkIcon className='size-4' />
                                  </Button>
                                )}
                              </div>
                            </FormControl>
                          </div>
                        ))}
                        <FormMessage />
                      </FormItem>
                      <div>
                        <Button
                          disabled={
                            field.disabled ||
                            selected?.length ===
                              availableDistributionMethods.length ||
                            ((field.name.includes('debt') ||
                              field.name.includes('reminder')) &&
                              selected?.length ===
                                availableDistributionMethods.slice(2).length) ||
                            disabled ||
                            !canHandleField
                          }
                          onClick={() =>
                            field.onChange([...(selected ?? []), []])
                          }
                          variant={'outline'}
                          type='button'
                        >
                          <PlusCircleIcon className='mr-2 size-4' /> Lägg till
                          ett försök
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          }}
        />
      </motion.div>
    </Card>
  );
}
